import { useState, useCallback, useEffect } from "react";
import { FileUploader } from "react-drag-drop-files";
import { MdCloudUpload } from "react-icons/md";
import Swal from "sweetalert2";

import {
  ACCEPTED_DOCUMENT_ACCEPT,
  normalizeIncomingFiles,
  isAcceptedDocumentFile,
  uploadDocumentQuick,
  formatUploadError,
  escapeHtml,
} from "./documentUploadUtils";

function DocumentDropzone({ projectId, clientId, categoryId, refreshData, disabled = false }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const canUpload = !disabled && (projectId || clientId);

  useEffect(() => {
    const input = document.querySelector(
      ".document-dropzone-uploader input[type='file']"
    );
    if (input) {
      input.setAttribute("accept", ACCEPTED_DOCUMENT_ACCEPT);
    }
  }, [canUpload, isUploading]);

  const uploadFiles = useCallback(
    async (incoming) => {
      if (!canUpload || isUploading) return;

      const files = normalizeIncomingFiles(incoming);
      const accepted = files.filter(isAcceptedDocumentFile);
      const rejected = files.filter((file) => !isAcceptedDocumentFile(file));

      if (accepted.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No valid files",
          html:
            `<p>Supported types: ${ACCEPTED_DOCUMENT_ACCEPT.replace(/\./g, "").replace(/,/g, ", ")}.</p>` +
            (rejected.length
              ? `<p>Skipped: ${rejected.map((f) => escapeHtml(f.name)).join(", ")}</p>`
              : ""),
        });
        return;
      }

      setIsUploading(true);
      setUploadProgress({ current: 0, total: accepted.length, failed: [] });

      const failed = [];
      for (let i = 0; i < accepted.length; i += 1) {
        const file = accepted[i];
        setUploadProgress({ current: i + 1, total: accepted.length, failed: [...failed] });

        try {
          await uploadDocumentQuick({ file, projectId, clientId, categoryId });
        } catch (error) {
          failed.push({ name: file.name, message: formatUploadError(error) });
        }
      }

      setIsUploading(false);
      setUploadProgress(null);

      if (refreshData) refreshData();

      const successCount = accepted.length - failed.length;
      const skippedNote =
        rejected.length > 0
          ? `<p>${rejected.length} file(s) skipped (unsupported type): ${rejected
              .map((f) => escapeHtml(f.name))
              .join(", ")}</p>`
          : "";
      const failedNote = failed
        .map(
          (f) =>
            `<div><strong>${escapeHtml(f.name)}</strong>: ${escapeHtml(f.message)}</div>`
        )
        .join("");

      if (failed.length === 0) {
        Swal.fire({
          icon: "success",
          title: "Upload complete",
          html:
            (successCount === 1
              ? "<p>1 document uploaded successfully.</p>"
              : `<p>${successCount} documents uploaded successfully.</p>`) + skippedNote,
          timer: rejected.length > 0 ? undefined : 2500,
          showConfirmButton: rejected.length > 0,
        });
      } else if (successCount === 0) {
        Swal.fire({
          icon: "error",
          title: "Upload failed",
          html: failedNote + skippedNote,
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Partial upload",
          html:
            `<p>${successCount} uploaded, ${failed.length} failed.</p>` +
            failedNote +
            skippedNote,
        });
      }
    },
    [canUpload, isUploading, projectId, clientId, categoryId, refreshData]
  );

  const contextLabel = projectId ? "this project" : clientId ? "this client" : "the selected record";

  return (
    <div style={{ margin: "16px 20px 0" }}>
      <div
        style={{
          opacity: canUpload && !isUploading ? 1 : 0.6,
          pointerEvents: canUpload && !isUploading ? "auto" : "none",
        }}
      >
        <FileUploader
          multiple
          handleChange={uploadFiles}
          name="documentFiles"
          disabled={!canUpload || isUploading}
          classes="document-dropzone-uploader"
        >
          <div
            style={{
              border: "2px dashed #93ab3c",
              borderRadius: 8,
              padding: "28px 20px",
              textAlign: "center",
              background: "#fafdf5",
              cursor: canUpload && !isUploading ? "pointer" : "not-allowed",
              transition: "background 0.15s ease, border-color 0.15s ease",
            }}
          >
            <MdCloudUpload style={{ fontSize: 40, color: "#93ab3c", marginBottom: 8 }} />
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
              Drag & drop documents here
            </div>
            <div style={{ color: "#666", fontSize: 14 }}>
              or click to browse — files upload to {contextLabel} with default settings
            </div>
            <div style={{ color: "#888", fontSize: 12, marginTop: 10 }}>
              Title from filename · trafficable on · original off · no clients, status, or expiry
            </div>
          </div>
        </FileUploader>
      </div>

      {isUploading && uploadProgress ? (
        <div style={{ marginTop: 10, fontSize: 14, color: "#555" }}>
          Uploading {uploadProgress.current} of {uploadProgress.total}…
        </div>
      ) : null}
    </div>
  );
}

export default DocumentDropzone;
